import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import keycloak from './keycloak';

const AuthContext = createContext(null);

const API_CLIENT_ID = 'waterco-api';

/*
 * Store the initialization promise outside the React component.
 * This prevents React rerenders from initializing Keycloak again.
 */
let keycloakInitPromise = null;

function initializeKeycloak() {
  if (!keycloakInitPromise) {
    keycloakInitPromise = keycloak
      .init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,

        /*
         * Always return to the React application root.
         * This URL must be permitted in Keycloak.
         */
        redirectUri: window.location.origin,
      })
      .catch((error) => {
        /*
         * Allow initialization to be retried if it failed.
         */
        keycloakInitPromise = null;
        throw error;
      });
  }

  return keycloakInitPromise;
}

export function AuthProvider({ children }) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [tokenParsed, setTokenParsed] = useState(null);
  const [authError, setAuthError] = useState(null);

  /*
   * Keep React state synchronized with the current Keycloak token.
   */
  const syncTokenState = useCallback(() => {
    const parsed = keycloak.tokenParsed ?? null;

    setTokenParsed(parsed);
    setAuthenticated(Boolean(keycloak.authenticated));

    if (parsed) {
      console.log('Keycloak user:', {
        username: parsed.preferred_username,
        issuer: parsed.iss,
        client: parsed.azp,

        // Safe for debugging. Do not log the actual access token.
        realmRoles: parsed.realm_access?.roles ?? [],
        apiRoles:
          parsed.resource_access?.[API_CLIENT_ID]?.roles ?? [],
      });
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const startAuthentication = async () => {
      try {
        setAuthError(null);

        const isAuthenticated = await initializeKeycloak();

        if (!mounted) {
          return;
        }

        setAuthenticated(Boolean(isAuthenticated));
        setTokenParsed(keycloak.tokenParsed ?? null);

        console.log('Keycloak initialized:', {
          authenticated: Boolean(isAuthenticated),
          username: keycloak.tokenParsed?.preferred_username,
          issuer: keycloak.tokenParsed?.iss,
          client: keycloak.tokenParsed?.azp,
          realmRoles:
            keycloak.tokenParsed?.realm_access?.roles ?? [],
          apiRoles:
            keycloak.tokenParsed?.resource_access?.[
              API_CLIENT_ID
            ]?.roles ?? [],
        });
      } catch (error) {
        console.error(
          'Keycloak initialization failed:',
          error,
        );

        if (mounted) {
          setAuthenticated(false);
          setTokenParsed(null);
          setAuthError(error);
        }
      } finally {
        if (mounted) {
          setInitialized(true);
        }
      }
    };

    keycloak.onAuthSuccess = () => {
      if (!mounted) {
        return;
      }

      syncTokenState();
      setAuthError(null);
    };

    keycloak.onAuthLogout = () => {
      if (!mounted) {
        return;
      }

      setAuthenticated(false);
      setTokenParsed(null);
    };

    keycloak.onAuthError = (error) => {
      console.error(
        'Keycloak authentication error:',
        error,
      );

      if (mounted) {
        setAuthError(error);
      }
    };

    keycloak.onTokenExpired = async () => {
      try {
        await keycloak.updateToken(30);

        if (mounted) {
          syncTokenState();
        }
      } catch (error) {
        console.error(
          'Keycloak token refresh failed:',
          error,
        );

        if (mounted) {
          setAuthenticated(false);
          setTokenParsed(null);
          setAuthError(error);
        }
      }
    };

    startAuthentication();

    return () => {
      mounted = false;
    };
  }, [syncTokenState]);

  const login = useCallback(() => {
    return keycloak.login({
      redirectUri: window.location.origin,
    });
  }, []);

  const logout = useCallback(() => {
    return keycloak.logout({
      redirectUri: window.location.origin,
    });
  }, []);

  const getAccessToken = useCallback(async () => {
    if (!keycloak.authenticated) {
      throw new Error(
        'The user is not authenticated.',
      );
    }

    try {
      await keycloak.updateToken(30);

      /*
       * updateToken() may replace tokenParsed,
       * so keep React state synchronized.
       */
      setTokenParsed(keycloak.tokenParsed ?? null);
    } catch (error) {
      console.error(
        'Unable to refresh Keycloak token:',
        error,
      );

      throw new Error(
        'Unable to refresh the authentication session.',
      );
    }

    if (!keycloak.token) {
      throw new Error(
        'Keycloak did not return an access token.',
      );
    }

    return keycloak.token;
  }, []);

  /*
   * Roles assigned specifically to waterco-api.
   *
   * Expected token structure:
   *
   * resource_access: {
   *   "waterco-api": {
   *     roles: ["admin"]
   *   }
   * }
   */
  const roles = useMemo(() => {
    return (
      tokenParsed?.resource_access?.[
        API_CLIENT_ID
      ]?.roles ?? []
    );
  }, [tokenParsed]);

  /*
   * Realm roles are kept separately.
   */
  const realmRoles = useMemo(() => {
    return tokenParsed?.realm_access?.roles ?? [];
  }, [tokenParsed]);

  /*
   * Check a waterco-api client role.
   */
  const hasRole = useCallback(
    (role) => {
      if (!role) {
        return false;
      }

      return roles.includes(role);
    },
    [roles],
  );

  /*
   * Admin authorization.
   *
   * This expects an "admin" client role on waterco-api.
   */
  const isAdmin = useMemo(() => {
    return roles.includes('admin');
  }, [roles]);

  const value = useMemo(
    () => ({
      initialized,
      authenticated,
      authError,

      user: tokenParsed,

      roles,
      realmRoles,
      isAdmin,

      login,
      logout,
      getAccessToken,
      hasRole,
    }),
    [
      initialized,
      authenticated,
      authError,
      tokenParsed,
      roles,
      realmRoles,
      isAdmin,
      login,
      logout,
      getAccessToken,
      hasRole,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used inside AuthProvider.',
    );
  }

  return context;
}